import { useSearchParams } from 'react-router';
import { lazyWithRetry } from '../../utils/lazyWithRetry';

export const QuickCapturePage = lazyWithRetry(() => import('../../pages/mobile/QuickCapturePage'));
const MagicLinkRedemption = lazyWithRetry(() =>
  import('../../pages/mobile/MagicLinkRedemption').then((m) => ({
    default: m.MagicLinkRedemption,
  })),
);

/**
 * `/m/capture` serves two jobs, and which one it is depends on `?token`, NOT on
 * whether the device has a session.
 *
 * A magic-link QR always points here (it's the redemption route), but the phone
 * scanning it is often ALREADY signed in — a user who scanned the onboarding
 * capture QR first, then scans the live QR. Without this branch that second
 * scan lands in the authenticated tree, where QuickCapturePage strips the token
 * and stays on the camera: the token is never redeemed, its purpose never
 * routes, and the user silently gets capture instead of the live session they
 * asked for.
 *
 * Used ONLY in the authenticated route tree. The unauthenticated tree binds
 * `/m/capture` straight to `MagicLinkRedemption` and that divergence is
 * deliberate: QuickCapturePage is a protected page, so a tokenless request from
 * a signed-out device must go to sign-in (which MagicLinkRedemption does, via
 * returnUrl), never render the camera.
 *
 * Extracted from App.tsx so this decision is directly testable — deleting the
 * branch is a silent regression of exactly the bug it exists to fix.
 */
export function MobileCaptureOrRedeem() {
  const [searchParams] = useSearchParams();
  // `get`, not `has`: MagicLinkRedemption gates on the value being TRUTHY, so
  // `?token=` (present but empty) would route there, bounce to /auth with this
  // URL as returnUrl, and be sent straight back — an infinite redirect.
  return searchParams.get('token') ? <MagicLinkRedemption /> : <QuickCapturePage />;
}
